import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage"
import imageCompression from "browser-image-compression"

/**
 * Upload multiple media (images + videos) to Firebase Storage with compression
 * VERSION WEB (React.js / browser)
 *
 * @param {string} entityType - 'event' ou 'company'
 * @param {string|number} entityId - ID de l'entité
 * @param {Array} mediasPicked - Array of { uri, file } or just File objects
 * @param {Function} onProgress - Callback pour progression (0-100)
 * @param {Function} setProgressMessage - Callback pour message de progression
 * @returns {Promise<Array>} Array of { path, downloadUrl }
 */
export const uploadMediaToFireStorage = async (
  entityType,
  entityId,
  mediasPicked,
  onProgress,
  setProgressMessage
) => {
  try {
    if (
      !mediasPicked ||
      (Array.isArray(mediasPicked) && mediasPicked.length === 0)
    ) {
      return []
    }

    const mediasArray = Array.isArray(mediasPicked) ? mediasPicked : [mediasPicked]
    const prefix = entityType === "event" ? "e" : "c"
    const storage = getStorage()

    let completed = 0
    const total = mediasArray.length

    const updateProgress = () => {
      const percent = Math.round((completed / total) * 100)
      if (onProgress) onProgress(percent)
    }

    const uploads = mediasArray.map(async (media, index) => {
      try {
        // Extraire le File object
        let file = media instanceof File ? media : media.file

        if (!file || !(file instanceof File)) {
          console.error("❌ Invalid file object:", media)
          throw new Error("Invalid file: must be a File instance")
        }

        console.log(`📤 Uploading media ${index + 1}/${total}:`, {
          name: file.name,
          size: file.size,
          type: file.type,
        })

        // Déterminer si c'est une vidéo ou une image
        const isVideo = file.type.startsWith("video/")
        const isImage = file.type.startsWith("image/")

        let fileToUpload = file
        let finalExtension = file.name.split(".").pop() || "jpg"

        // 🔹 Compression pour les images uniquement
        if (isImage && setProgressMessage) {
          setProgressMessage(`Optimisation image ${index + 1}/${total}...`)

          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1280,
            useWebWorker: true,
            fileType: "image/jpeg",
          }

          try {
            const compressedFile = await imageCompression(file, options)

            console.log(`✅ Compression réussie:`, {
              before: (file.size / 1024 / 1024).toFixed(2) + " MB",
              after: (compressedFile.size / 1024 / 1024).toFixed(2) + " MB",
            })

            fileToUpload = compressedFile
            finalExtension = "jpg"
          } catch (compressionError) {
            console.warn("⚠️ Compression failed, using original:", compressionError)
            // Continue avec le fichier original
          }
        }

        // 🔹 Générer le nom et le path Firebase
        const timestamp = Date.now()
        const randomId = Math.random().toString(36).substring(2, 9)
        const fileName = `${prefix}${entityId}_${timestamp}_${randomId}.${finalExtension}`
        const path = `${entityType}/${entityId}/${fileName}`

        console.log(`📦 Firebase path: ${path}`)

        // 🔹 Créer la référence Firebase
        const storageRef = ref(storage, path)

        // 🔹 Métadonnées
        const metadata = {
          contentType: fileToUpload.type || (isVideo ? "video/mp4" : "image/jpeg"),
          customMetadata: {
            entityType,
            entityId: String(entityId),
            uploadedAt: new Date().toISOString(),
          },
        }

        // 🔹 Upload avec suivi de progression
        if (setProgressMessage) {
          setProgressMessage(`Upload ${index + 1}/${total}...`)
        }

        const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata)

        // Promise pour gérer l'upload
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              const overallProgress = ((completed + progress / 100) / total) * 100

              if (onProgress) {
                onProgress(Math.round(overallProgress))
              }

              console.log(`📊 Upload progress: ${Math.round(progress)}%`)
            },
            (error) => {
              console.error("❌ Upload error:", error)
              reject(error)
            },
            async () => {
              console.log("✅ Upload complete, getting download URL...")
              resolve()
            }
          )
        })

        // 🔹 Récupérer l'URL de téléchargement
        const downloadUrl = await getDownloadURL(storageRef)

        console.log("✅ Download URL obtained:", downloadUrl)

        completed++
        updateProgress()

        return { path, downloadUrl }
      } catch (error) {
        console.error(`❌ Error uploading media ${index + 1}:`, error)
        throw error
      }
    })

    const results = await Promise.all(uploads)

    if (onProgress) onProgress(100)
    if (setProgressMessage) setProgressMessage("Upload terminé !")

    console.log(`✅ All uploads complete:`, results)

    return results
  } catch (error) {
    console.error("❌ Error in uploadMediaToFireStorage:", error)
    if (onProgress) onProgress(0)
    return []
  }
}

/**
 * Get full cover image URL from API response
 */
export const getFullCoverImageUrl = (response) => {
  if (
    !response?.images ||
    !Array.isArray(response.images) ||
    response.images.length === 0
  ) {
    return null
  }
  const cover = response.images.find((img) => img.isCover === true)
  return cover ? cover : response.images[0]
}

/**
 * Get gallery images (non-cover) from API response
 */
export const getGalleryImagesUrl = (response) => {
  if (
    !response?.images ||
    !Array.isArray(response.images) ||
    response.images.length === 0
  ) {
    return []
  }
  return response.images.filter((img) => img.isCover === false)
}

/**
 * Get all images (with cover) from API response
 */
export const getGalleryImagesUrlWithCover = (response) => {
  if (
    !response?.images ||
    !Array.isArray(response.images) ||
    response.images.length === 0
  ) {
    return []
  }
  return response.images
}

/**
 * Detect media type from URI or MIME
 */
export const detectMediaType = (uri = "", mime = "") => {
  if (!uri) return "image"

  // 1️⃣ MIME type is most reliable
  if (mime.startsWith("video/")) return "video"
  if (mime.startsWith("image/")) return "image"

  // 2️⃣ Try to decode URL
  try {
    const decoded = decodeURIComponent(uri.toLowerCase())
    if (
      decoded.includes(".mp4") ||
      decoded.includes(".mov") ||
      decoded.includes(".avi") ||
      decoded.includes(".mkv")
    )
      return "video"
    if (
      decoded.includes(".jpg") ||
      decoded.includes(".jpeg") ||
      decoded.includes(".png") ||
      decoded.includes(".webp")
    )
      return "image"
  } catch {
    // Not blocking
  }

  // 3️⃣ Direct URI check
  const lower = uri.toLowerCase()
  if (
    lower.includes(".mp4") ||
    lower.includes(".mov") ||
    lower.includes(".avi") ||
    lower.includes(".mkv")
  )
    return "video"
  if (
    lower.includes(".jpg") ||
    lower.includes(".jpeg") ||
    lower.includes(".png") ||
    lower.includes(".webp")
  )
    return "image"

  // 4️⃣ Default to image
  return "image"
}

// import {
//   getStorage,
//   ref,
//   uploadBytesResumable,
//   getDownloadURL,
// } from "firebase/storage"
// import imageCompression from "browser-image-compression"
//
// // Fonction pour générer un UUID simple (sans dépendance externe)
// const generateUUID = () => {
//   return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
//     const r = (Math.random() * 16) | 0
//     const v = c === "x" ? r : (r & 0x3) | 0x8
//     return v.toString(16)
//   })
// }
//
// /**
//  * Compresse une image pour le web
//  * @param {File} file - Fichier image à compresser
//  * @returns {Promise<File>} - Fichier compressé
//  */
// const compressImage = async (file) => {
//   try {
//     const options = {
//       maxSizeMB: 1,
//       maxWidthOrHeight: 1280,
//       useWebWorker: true,
//       fileType: "image/jpeg",
//     }
//
//     const compressedFile = await imageCompression(file, options)
//
//     console.log("Avant compression :", (file.size / 1024 / 1024).toFixed(2), "Mo")
//     console.log(
//       "Après compression :",
//       (compressedFile.size / 1024 / 1024).toFixed(2),
//       "Mo"
//     )
//
//     return compressedFile
//   } catch (error) {
//     console.error("Erreur compression :", error)
//     return file // Retourne le fichier original si erreur
//   }
// }
//
// /**
//  * Détecte le type de média (image ou vidéo)
//  * @param {File} file - Fichier à analyser
//  * @returns {string} - 'image' ou 'video'
//  */
// const detectMediaType = (file) => {
//   if (!file || !file.type) return "image"
//   return file.type.startsWith("video/") ? "video" : "image"
// }
//
// /**
//  * Upload un ou plusieurs médias vers Firebase Storage
//  * @param {string} entityType - Type d'entité ('event' ou 'company')
//  * @param {string|number} entityId - ID de l'entité
//  * @param {File|File[]} mediaFiles - Fichier(s) à uploader
//  * @param {Function} onProgress - Callback de progression (0-100)
//  * @param {Function} setProgressMessage - Callback pour le message de progression
//  * @returns {Promise<Array>} - Tableau d'objets {path, downloadUrl}
//  */
// export const uploadMediaToFireStorage = async (
//   entityType,
//   entityId,
//   mediaFiles,
//   onProgress = null,
//   setProgressMessage = null
// ) => {
//   try {
//     if (!mediaFiles || (Array.isArray(mediaFiles) && mediaFiles.length === 0)) {
//       return []
//     }
//
//     const storage = getStorage()
//     const filesArray = Array.isArray(mediaFiles) ? mediaFiles : [mediaFiles]
//     const prefix = entityType === "event" ? "e" : "c"
//
//     let completed = 0
//     const total = filesArray.length
//
//     const updateProgress = () => {
//       const percent = Math.round((completed / total) * 100)
//       if (onProgress) onProgress(percent)
//     }
//
//     const uploads = filesArray.map(async (file) => {
//       const mediaType = detectMediaType(file)
//       const isVideo = mediaType === "video"
//
//       console.log(`Uploading: ${file.name} (type: ${mediaType})`)
//
//       // --- 🧩 Étape 1 : compression (seulement pour les images) ---
//       if (setProgressMessage) setProgressMessage("Optimisation des médias...")
//
//       let fileToUpload = file
//       if (!isVideo) {
//         fileToUpload = await compressImage(file)
//       }
//
//       // --- 🧩 Étape 2 : préparation du chemin Firebase ---
//       const extOut = isVideo ? "mp4" : "jpg"
//       const fileName = `${prefix}${entityId}_${generateUUID()}.${extOut}`
//       const path = `${entityType}/${entityId}/${fileName}`
//       const storageRef = ref(storage, path)
//
//       // --- 🧩 Étape 3 : upload vers Firebase ---
//       if (setProgressMessage) setProgressMessage("Upload en cours...")
//
//       const metadata = {
//         contentType: isVideo ? file.type : "image/jpeg",
//       }
//
//       const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata)
//
//       // Promise pour gérer l'upload
//       const uploadPromise = new Promise((resolve, reject) => {
//         uploadTask.on(
//           "state_changed",
//           (snapshot) => {
//             const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
//             if (onProgress) {
//               onProgress(Math.round(progress / total))
//             }
//           },
//           (error) => {
//             console.error("Erreur upload :", error)
//             reject(error)
//           },
//           async () => {
//             try {
//               const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)
//               completed++
//               updateProgress()
//               resolve({ path, downloadUrl })
//             } catch (error) {
//               reject(error)
//             }
//           }
//         )
//       })
//
//       return uploadPromise
//     })
//
//     const results = await Promise.all(uploads)
//     if (onProgress) onProgress(100)
//     return results
//   } catch (error) {
//     console.error("Erreur upload médias :", error)
//     if (onProgress) onProgress(0)
//     return []
//   }
// }
//
// /**
//  * Récupère l'URL de la cover d'un event/company
//  * @param {Object} response - Réponse de l'API contenant les images
//  * @returns {Object|null} - Objet image cover ou null
//  */
// export const getFullCoverImageUrl = (response) => {
//   if (
//     !response?.images ||
//     !Array.isArray(response.images) ||
//     response.images.length === 0
//   ) {
//     return null
//   }
//   const cover = response.images.find((img) => img.isCover === true)
//   return cover || response.images[0]
// }
//
// /**
//  * Récupère toutes les images de la galerie (sans la cover)
//  * @param {Object} response - Réponse de l'API
//  * @returns {Array|null} - Tableau d'images ou null
//  */
// export const getGalleryImagesUrl = (response) => {
//   if (
//     !response?.images ||
//     !Array.isArray(response.images) ||
//     response.images.length === 0
//   ) {
//     return null
//   }
//   return response.images.filter((img) => img.isCover === false) || null
// }
//
// /**
//  * Récupère toutes les images (galerie + cover)
//  * @param {Object} response - Réponse de l'API
//  * @returns {Array|null} - Tableau d'images ou null
//  */
// export const getGalleryImagesUrlWithCover = (response) => {
//   if (
//     !response?.images ||
//     !Array.isArray(response.images) ||
//     response.images.length === 0
//   ) {
//     return null
//   }
//   return response.images || null
// }
